<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import TheHeader from '@/components/basic/organisms/TheHeader.vue'
import TheCategoryTemplate from '@/components/category/templates/TheCategoryTemplate.vue'
import { EXPIRED_CAMPAIGN_PAGE_IDS } from '@/constants/CategoryPageConstants'
import {
  containsPrivateInfo,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import SiteConfig from '@/services/SiteConfig'
import { useCustomerStore } from '@/stores/customer'
import { useSearchConfig } from '@/stores/searchConfig'
import { useHead } from '@unhead/vue'
import { computed, defineAsyncComponent, nextTick, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import { useRecommendationStore } from '../stores/recommendations.js'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const AlertBanner = defineAsyncComponent(
  () => import('@/components/basic/atoms/AlertBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)

const searchConfig = useSearchConfig()
const { fetchCategory, resetConfig } = searchConfig
const customerStore = useCustomerStore()
const router = useRouter()
const route = useRoute()

const category = computed(() => searchConfig.category)
const getConfig = computed(() => searchConfig.getConfig)
const title = computed(
  () => category.value?.meta_title || category.value?.name || ''
)
const pageIsVisible = computed(
  () => getConfig.value?.is_enabled ?? category.value?.is_enabled ?? false
)

watch(
  [pageIsVisible, getConfig],
  async ([isVisible]) => {
    if (!getConfig.value?.category || isVisible) return

    await customerStore.waitUntilPending()
    if (customerStore.customerIsAdmin) return

    const pageId = EXPIRED_CAMPAIGN_PAGE_IDS[SiteConfig.locale]
    if (pageId) {
      await router.replace(`/kampagne-abgelaufen-cms-p${pageId}`)
    }
  },
  { immediate: false }
)

const recommendations = useRecommendationStore()
recommendations.fetch()

fetchCategory()

let lastId = route.params.id

watch(
  () => [route.params.id, JSON.stringify(route.query)],
  async ([id]) => {
    // Reload whenever the URL changes — including browser back/forward, where
    // the query reverts but nothing else triggers a fetch (VIP-11940). A full
    // reset is only needed when the category itself changes; query-only changes
    // (filters/sort/pagination) keep the page and just refetch. The matching
    // imperative fetch from applyFilter is de-duped in the store.
    if (id !== lastId) {
      lastId = id
      resetConfig()
    }
    await nextTick()
    await fetchCategory()
  }
)

onBeforeRouteLeave(() => {
  resetConfig()
})

useHead({
  title,
  meta: [
    {
      name: 'robots',
      content: computed(() =>
        containsPrivateInfo() ? NO_INDEX_FOLLOW : category.value?.robots
      )
    },
    {
      name: 'description',
      content: computed(() => category.value?.meta_description)
    }
  ],
  link: [
    {
      rel: 'canonical',
      href: computed(() => category.value?.canonical_url || route.path)
    }
  ]
})
</script>

<template>
  <div
    class="position-relative"
    id="content"
    data-elbglobals="page_type:category"
    data-elbcontext="shopping:discover"
  >
    <AppBanner />
    <AlertBanner
      v-if="!pageIsVisible"
      :text="translate('alert_message_preview_mode')"
    />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader class="position-relative" />
    <main id="page-template">
      <TheCategoryTemplate />
    </main>
    <LazyComponent id="footer" :delay="2000">
      <TheFooter />
    </LazyComponent>
  </div>
</template>
